import type { FinancialsGroupDimension } from '@siliconx/shared';

/**
 * Epic 3 (lots-first-class plan) — the ONE "Group by" control shared by
 * every point-in-time financial/FMV surface that used to be hardcoded to
 * asset class (Financial Position summary, Opportunity Cost). Deliberately
 * a single parameterized control, not a tab/page per dimension — separate
 * surfaces would triple the maintenance and drift apart (plan "Deliverables
 * §1").
 *
 * Time-series charts (the portfolio FMV daily chart) never get this control
 * — lot/team membership has no history, so a per-group series would chart
 * today's membership against historical prices (plan "Epic 3 — scope").
 *
 * Styling matches the existing `<select>` filter convention (see
 * `routes/assets/index.tsx`'s `SELECT_CLASS`) rather than inventing a new
 * segmented-control pattern.
 */
const SELECT_CLASS =
  'rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-sm text-zinc-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 cursor-pointer';

const DIMENSION_OPTIONS: Array<{ value: FinancialsGroupDimension; label: string }> = [
  { value: 'class', label: 'Asset class' },
  { value: 'lot', label: 'Lot' },
  { value: 'team', label: 'Team' },
  { value: 'site', label: 'Site' },
];

export function GroupByControl({
  value,
  onChange,
}: {
  value: FinancialsGroupDimension;
  onChange: (dimension: FinancialsGroupDimension) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm text-muted-foreground">
      <span className="font-medium">Group by</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as FinancialsGroupDimension)}
        className={SELECT_CLASS}
        aria-label="Group by"
      >
        {DIMENSION_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
